import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface SpendingAlertConfig {
  enabled: boolean;
  warning_pct: number;   // default 70
  danger_pct: number;    // default 90
  custom_income: number | null; // null = usar receitas reais do mês
}

export type AlertLevel = 'ok' | 'warning' | 'danger';

export interface SpendingAlertState {
  level: AlertLevel;
  pctUsed: number;          // % do "orçamento" já gasto
  monthlyExpenses: number;
  referenceIncome: number;  // receita usada como referência
  usingCustomIncome: boolean;
  config: SpendingAlertConfig;
  loading: boolean;
  saving: boolean;
  saveError: string | null; // non-null when last save failed
  saveConfig: (cfg: SpendingAlertConfig) => Promise<boolean>; // true = saved, false = error
}

const DEFAULT_CONFIG: SpendingAlertConfig = {
  enabled: false,
  warning_pct: 70,
  danger_pct: 90,
  custom_income: null,
};

export const useSpendingAlerts = (
  monthlyExpenses: number,
  monthlyIncome: number
): SpendingAlertState => {
  const { user } = useAuth();
  const [config, setConfig] = useState<SpendingAlertConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ─── Load config from profiles ───────────────────────────────────────────
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('profiles')
        .select('spending_alert_config')
        .eq('id', user.id)
        .maybeSingle();

      if (data?.spending_alert_config) {
        setConfig({ ...DEFAULT_CONFIG, ...data.spending_alert_config });
      }
      setLoading(false);
    })();
  }, [user]);

  // ─── Save config to profiles ──────────────────────────────────────────────
  const saveConfig = useCallback(async (cfg: SpendingAlertConfig): Promise<boolean> => {
    if (!user) return false;
    setSaving(true);
    setSaveError(null);

    // Optimistic update so the UI feels instant
    const previousConfig = config;
    setConfig(cfg);

    const { error } = await supabase
      .from('profiles')
      .update({ spending_alert_config: cfg, updated_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) {
      // Revert optimistic update on failure
      console.error('[useSpendingAlerts] saveConfig failed:', error.message);
      setConfig(previousConfig);
      setSaveError(error.message);
      setSaving(false);
      return false;
    }

    setSaving(false);
    return true;
  }, [user, config]);

  // ─── Compute alert level ──────────────────────────────────────────────────
  const referenceIncome =
    config.custom_income && config.custom_income > 0
      ? config.custom_income
      : monthlyIncome;

  const usingCustomIncome = !!(config.custom_income && config.custom_income > 0);

  let pctUsed = 0;
  let level: AlertLevel = 'ok';

  if (config.enabled && referenceIncome > 0) {
    pctUsed = Math.round((monthlyExpenses / referenceIncome) * 100);
    if (pctUsed >= config.danger_pct) {
      level = 'danger';
    } else if (pctUsed >= config.warning_pct) {
      level = 'warning';
    }
  }

  return {
    level,
    pctUsed,
    monthlyExpenses,
    referenceIncome,
    usingCustomIncome,
    config,
    loading,
    saving,
    saveError,
    saveConfig,
  };
};
